import { useCallback, useMemo, useState } from "react";
import { modules, entities, currencyToCents } from "byzantine";
import { useInvoiceFlow } from "./InvoiceFlowContext";
import type { HighlightLocation } from "./types";

export type InvoiceHighlightField = "amount" | "date" | "memo";

export type InvoiceFieldHandlers = {
  onFocus: () => void;
  onBlur: () => void;
};

const FIELDS: InvoiceHighlightField[] = ["amount", "date", "memo"];

const useInvoiceHighlight = () => {
  const { invoice } = useInvoiceFlow();
  const form = modules.achPayments.ACHPaymentForm.useForm();
  const { values } = form;
  const [focusedField, setFocusedField] =
    useState<InvoiceHighlightField | null>(null);

  const extracted = invoice?.extracted;

  const locations = useMemo(
    () => entities.invoices.getInvoiceFieldLocations(extracted),
    [extracted]
  );
  const defaults = useMemo(
    () => entities.invoices.getInvoiceDetailsFormDefaults(extracted),
    [extracted]
  );

  // does the field still hold exactly what the extractor found?
  const matchesExtracted = useCallback(
    (field: InvoiceHighlightField) => {
      switch (field) {
        case "amount":
          // the form holds cents; the extracted value is a decimal string
          return (
            !!defaults.amount &&
            values.amount === currencyToCents(defaults.amount)
          );
        case "date":
          return !!defaults.date && values.startDate === defaults.date;
        case "memo":
          return !!defaults.memo && values.description === defaults.memo;
        default:
          return false;
      }
    },
    [defaults, values.amount, values.startDate, values.description]
  );

  const highlight: HighlightLocation | null =
    focusedField && matchesExtracted(focusedField)
      ? locations[focusedField]
      : null;

  const onFieldFocus = useCallback(
    (field: InvoiceHighlightField) => () => setFocusedField(field),
    []
  );

  // blur fires before the next field's focus, so only clear if nothing else
  // has claimed focus in the meantime
  const onFieldBlur = useCallback(
    (field: InvoiceHighlightField) => () =>
      setFocusedField((current) => (current === field ? null : current)),
    []
  );

  // `onFieldFocus(field)` builds a new closure on every render, which would
  // defeat `React.memo` on any field it is passed to. `DateInput` re-initializes
  // flatpickr on every render and destroys any open calendar, so it must not
  // re-render when focus changes — hand out a stable handler pair per field.
  const fieldHandlers = useMemo(
    () =>
      FIELDS.reduce(
        (handlers, field) => ({
          ...handlers,
          [field]: {
            onFocus: onFieldFocus(field),
            onBlur: onFieldBlur(field),
          },
        }),
        {} as Record<InvoiceHighlightField, InvoiceFieldHandlers>
      ),
    [onFieldFocus, onFieldBlur]
  );

  const clearHighlight = useCallback(() => setFocusedField(null), []);

  return { highlight, fieldHandlers, clearHighlight };
};

export default useInvoiceHighlight;
